import {isOriginAnchor} from './isOriginAnchor';
import {CsvConfigJsonResult} from '../synoptic-configuration-csv-types';

export function isStyleByContinuousValueArgs(obj: any): CsvConfigJsonResult {
  let valid = true;
  let errors = [];

  const resultStyleRange = isStyleRange(obj);
  if (!resultStyleRange.result) {
    valid = false;
    errors = [...errors, ...resultStyleRange.errors];
  }

  if (obj.valueRange === undefined) {
    valid = false;
    errors.push({code: 'notExist', key: 'valueRange'});
  } else {
    const resultIsNumberArray = isNumberArray(obj.valueRange, 'valueRange');
    if (!resultIsNumberArray.result) {
      valid = false;
      errors = [...errors, ...resultIsNumberArray.errors];
    }
  }

  if (!Array.isArray(obj.attributes)) {
    errors.push({code: 'notArray', key: 'attributes'});
    valid = false;
  } else {
    const resultAttributes = isStyleRangeAttribute(obj.attributes);
    if (!resultAttributes.result) {
      valid = false;
      errors = [...errors, ...resultAttributes.errors];
    }
  }

  if (obj.originAnchor === undefined) {
    valid = false;
    errors.push({code: 'notExist', key: 'originAnchor'});
  } else {
    const resultIsOriginAnchor = isOriginAnchor(obj.originAnchor);
    if (!resultIsOriginAnchor.result) {
      valid = false;
      errors = [...errors, ...resultIsOriginAnchor.errors];
    }
  }

  return {result: valid, errors: errors};
}

function isNumberArray(arr: any, key: string): CsvConfigJsonResult {
  let valid = true;
  let errors = [];

  if (!Array.isArray(arr)) {
    valid = false;
    errors.push({code: 'notArray', key: key});
  } else {
    const resultMinLen = isArrayMinLength(arr, key, 2);
    valid = resultMinLen.result;
    errors = [...errors, ...resultMinLen.errors];
    arr.forEach((item) => {
      if (typeof item !== 'number') {
        valid = false;
        errors.push({code: 'notText', key: key, value: item});
      }
    });
  }

  return {result: valid, errors: errors};
}

function isNumberArrayAndPercentage(arr: any, key: string): CsvConfigJsonResult {
  let valid = true;
  let errors = [];

  if (!Array.isArray(arr)) {
    valid = false;
    errors.push({code: 'notArray', key: key});
  } else {
    const resultMinLen = isArrayMinLength(arr, key, 2);
    valid = resultMinLen.result;
    errors = [...errors, ...resultMinLen.errors];
    arr.forEach((item) => {
      if (typeof item !== 'number') {
        valid = false;
        errors.push({code: 'notText', key: key, value: item});
      } else if (item > 100 || item < 0) {
        valid = false;
        errors.push({code: 'invalidPercentage', key: key, value: item});
      }
    });
  }

  return {result: valid, errors: errors};
}

function isStyleRangeAttribute(arr: any): CsvConfigJsonResult {
  let valid: boolean = true;
  let errors = [];
  const validProperties = ['backgroundColor', 'widthPercentage', 'heightPercentage', 'opacityPercentage'];
  arr.forEach((item) => {
    if (!validProperties.includes(item)) {
      valid = false;
      errors.push({code: 'invalidValueArray', key: 'attributes', value: item});
    }
  });

  return {result: valid, errors: errors};
}

function isStyleRange(obj: any): CsvConfigJsonResult {
  let valid = true;
  let errors = [];
  let attributes: any[] | undefined = obj.attributes;

  if (obj.backgroundColorRange !== undefined) {
    if (!attributes?.includes('backgroundColor')) {
      valid = false;
      errors.push({code: 'notDefinedBackgroundColor', key: 'backgroundColorRange'});
    }
    const result = isStringArray(obj.backgroundColorRange, 'backgroundColorRange');
    if (!result.result) {
      valid = false;
      errors = [...errors, ...result.errors];
    }
  } else {
    if (attributes?.includes('backgroundColor')) {
      valid = false;
      errors.push({code: 'notExist', key: 'backgroundColorRange'});
    }
  }

  if (obj.widthPercentageRange !== undefined) {
    if (!attributes?.includes('widthPercentage')) {
      valid = false;
      errors.push({code: 'notDefinedWidthPercentage', key: 'widthPercentageRange'});
    }
    const result = isNumberArrayAndPercentage(obj.widthPercentageRange, 'widthPercentageRange');
    if (!result.result) {
      valid = false;
      errors = [...errors, ...result.errors];
    }
  } else if (attributes?.includes('widthPercentage')) {
    valid = false;
    errors.push({code: 'notExist', key: 'widthPercentageRange'});
  }

  if (obj.heightPercentageRange !== undefined) {
    if (!attributes?.includes('heightPercentage')) {
      valid = false;
      errors.push({code: 'notDefinedHeightPercentage', key: 'heightPercentageRange'});
    }
    const result = isNumberArrayAndPercentage(obj.heightPercentageRange, 'heightPercentageRange');
    if (!result.result) {
      valid = false;
      errors = [...errors, ...result.errors];
    }
  } else if (attributes?.includes('heightPercentage')) {
    valid = false;
    errors.push({code: 'notExist', key: 'heightPercentageRange'});
  }

  if (obj.opacityPercentageRange !== undefined) {
    if (!attributes?.includes('opacityPercentage')) {
      valid = false;
      errors.push({code: 'notDefinedOpacityPercentage', key: 'opacityPercentage'});
    }
    const result = isNumberArrayAndPercentage(obj.opacityPercentageRange, 'opacityPercentageRange');
    if (!result.result) {
      valid = false;
      errors = [...errors, ...result.errors];
    }
  } else if (attributes?.includes('opacityPercentage')) {
    valid = false;
    errors.push({code: 'notExist', key: 'opacityPercentageRange'});
  }

  return {result: valid, errors: errors};
}

function isStringArray(arr: any, key: string): CsvConfigJsonResult {
  let valid = true;
  let errors = [];

  if (!Array.isArray(arr)) {
    valid = false;
    errors.push({code: 'notArray', key: key});
  } else {
    const resultMinLen = isArrayMinLength(arr, key, 2);
    valid = resultMinLen.result;
    errors = [...errors, ...resultMinLen.errors];
    arr.forEach((item) => {
      if (typeof item !== 'string') {
        valid = false;
        errors.push({code: 'notText', key: key, value: item});
      }
    });
  }

  return {result: valid, errors: errors};
}

function isArrayMinLength(arr: any[], key: string, minLen: number): CsvConfigJsonResult {
  let valid = true;
  const errors = [];
  if (arr.length !== minLen) {
    valid = false;
    errors.push({code: 'arrayLengthMin2', key: key, value: arr});
  }
  return {result: valid, errors: errors};
}
